import React, { useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPinIcon, PhoneIcon, ClockIcon, NavigationIcon, CalendarIcon } from 'lucide-react';
const communities = ['Hurst', 'Colleyville', 'Bedford', 'Euless', 'North Richland Hills'];
export function LocationSection() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section id="location" className="py-20 md:py-28 bg-white" data-id="element-326">
      <div className="max-w-7xl mx-auto px-4 sm:px-6" ref={ref} data-id="element-327">
        <motion.div className="text-center max-w-4xl mx-auto mb-14 md:mb-16" initial={{
        opacity: 0,
        y: 24
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} data-id="element-328">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-navy-950 font-heading tracking-tight" data-id="element-329">
            Our Hurst Academy, Convenient to Colleyville Families
          </h2>
          <p className="mt-4 text-lg text-ink-muted leading-relaxed" data-id="element-330">
            Located on Grapevine Hwy in Hurst — just minutes from Colleyville,
            Bedford, Euless and North Richland Hills.
          </p>
        </motion.div>

        <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12" initial={{
        opacity: 0,
        y: 24
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.1
      }} data-id="element-331">
          {/* Info */}
          <div className="space-y-8" data-id="element-332">
            {/* Address */}
            <div className="flex gap-4" data-id="element-333">
              <div className="shrink-0 w-10 h-10 rounded-lg bg-navy-950 flex items-center justify-center" data-id="element-334">
                <MapPinIcon className="w-5 h-5 text-amber" data-id="element-335" />
              </div>
              <div data-id="element-336">
                <h3 className="text-base font-bold text-navy-950" data-id="element-337">
                  NTA Taekwondo
                </h3>
                <p className="text-sm text-ink-muted mt-1" data-id="element-338">
                  750 Grapevine Hwy, Hurst, TX 76054
                </p>
                <p className="text-xs text-ink-muted mt-0.5" data-id="element-339">
                  Serving Colleyville and nearby areas from our Hurst location
                </p>
              </div>
            </div>

            {/* Phone */}
            <div className="flex gap-4" data-id="element-340">
              <div className="shrink-0 w-10 h-10 rounded-lg bg-navy-950 flex items-center justify-center" data-id="element-341">
                <PhoneIcon className="w-5 h-5 text-amber" data-id="element-342" />
              </div>
              <div data-id="element-343">
                <h3 className="text-base font-bold text-navy-950" data-id="element-344">Call Us</h3>
                <a href="tel:817-581-1419" className="text-sm text-amber-dark font-semibold hover:text-amber transition-colors" data-id="element-345">
                  817-581-1419
                </a>
              </div>
            </div>

            {/* Hours placeholder */}
            <div className="flex gap-4" data-id="element-346">
              <div className="shrink-0 w-10 h-10 rounded-lg bg-navy-950 flex items-center justify-center" data-id="element-347">
                <ClockIcon className="w-5 h-5 text-amber" data-id="element-348" />
              </div>
              <div data-id="element-349">
                <h3 className="text-base font-bold text-navy-950" data-id="element-350">Hours</h3>
                <p className="text-sm text-ink-muted mt-1" data-id="element-351">
                  Contact us for current class schedule and hours
                </p>
              </div>
            </div>

            {/* Since 2008 */}
            <div className="flex gap-4" data-id="element-352">
              <div className="shrink-0 w-10 h-10 rounded-lg bg-navy-950 flex items-center justify-center" data-id="element-353">
                <CalendarIcon className="w-5 h-5 text-amber" data-id="element-354" />
              </div>
              <div data-id="element-355">
                <h3 className="text-base font-bold text-navy-950" data-id="element-356">
                  Established 2008
                </h3>
                <p className="text-sm text-ink-muted mt-1" data-id="element-357">
                  Proudly serving local families for over 17 years
                </p>
              </div>
            </div>

            {/* Communities served */}
            <div data-id="element-358">
              <h3 className="text-sm font-bold text-navy-950 uppercase tracking-wider mb-3" data-id="element-359">
                Proudly Serving
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-360">
                {communities.map(community => <span key={community} className="inline-flex items-center gap-1 px-3 py-1.5 bg-warm-100 border border-warm-200 rounded-full text-sm text-ink font-medium" data-id="element-361">
                    <NavigationIcon className="w-3 h-3 text-amber-dark" data-id="element-362" />
                    {community}
                  </span>)}
              </div>
            </div>
          </div>

          {/* Google Map */}
          <div className="rounded-2xl overflow-hidden border border-warm-200 min-h-[320px] lg:min-h-[400px]" data-id="element-363">
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d107245.97549239715!2d-97.26421926580795!3d32.860162379731!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864e7f6439324fb7%3A0xf55fd5132501d9df!2sNTA%20Taekwondo-Hurst!5e0!3m2!1sen!2sus!4v1774289767859!5m2!1sen!2sus" width="100%" height="100%" style={{
            border: 0,
            minHeight: '320px'
          }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="NTA Taekwondo location on Google Maps" className="w-full h-full min-h-[320px] lg:min-h-[400px]" data-id="element-364" />
          </div>
        </motion.div>
      </div>
    </section>;
}